import axios from 'axios';

const API_BASE = 'https://fi.erp-x.com/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('ctv_token') || sessionStorage.getItem('ctv_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      localStorage.removeItem('ctv_token');
      localStorage.removeItem('ctv_userInfo');
      sessionStorage.removeItem('ctv_token');
      sessionStorage.removeItem('ctv_userInfo');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
};

export const dashboardAPI = {
  getStats: () => api.get('/ctv/dashboard'),
  getBanks: () => api.get('/ctv/dashboard/banks'),
};

export const customerAPI = {
  getAll: (params) => api.get('/ctv/customers', { params }),
  getOne: (id) => api.get(`/ctv/customers/${id}`),
  create: (data) => api.post('/ctv/customers', data),
  update: (id, data) => api.put(`/ctv/customers/${id}`, data),
  delete: (id) => api.delete(`/ctv/customers/${id}`),
};

export const loanAPI = {
  getAll: (params) => api.get('/ctv/loans', { params }),
  getOne: (id) => api.get(`/ctv/loans/${id}`),
  create: (data) => api.post('/ctv/loans', data),
  update: (id, data) => api.put(`/ctv/loans/${id}`, data),
  delete: (id) => api.delete(`/ctv/loans/${id}`),

  // Draft loans
  getDrafts: () => api.get('/ctv/loans/drafts'),
  saveDraft: (data) => api.post('/ctv/loans/drafts', data),
  updateDraft: (id, data) => api.put(`/ctv/loans/drafts/${id}`, data),
  deleteDraft: (id) => api.delete(`/ctv/loans/drafts/${id}`),

  // Loan details
  getPaymentSchedule: (id) => api.get(`/ctv/loans/${id}/payment-schedule`),
};

export const commissionAPI = {
  getAll: (params) => api.get('/ctv/commissions', { params }),
  getDetails: (month, year) => api.get('/ctv/commissions/details', { params: { month, year } }),
  getClawbacks: () => api.get('/ctv/commissions/clawbacks'),
};

export const paymentRequestAPI = {
  getAll: () => api.get('/ctv/payment-requests'),
  create: (data) => api.post('/ctv/payment-requests', data),
  getOne: (id) => api.get(`/ctv/payment-requests/${id}`),
};

export const notificationAPI = {
  getAll: (params) => api.get('/ctv/notifications', { params }),
  markAsRead: (id) => api.put(`/ctv/notifications/${id}/read`),
  markAllAsRead: () => api.put('/ctv/notifications/read-all'),
  subscribe: (subscription) => api.post('/ctv/notifications/subscribe', subscription),
};

export const uploadAPI = {
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
  },
};

export const configAPI = {
  getCountries: (params) => api.get('/countries', { params }),
  getVisaTypes: (params) => api.get('/visa-types', { params }),
};

export const bankAPI = {
  getAll: () => api.get('/banks'),
  getBranches: (bankId) => api.get(`/banks/${bankId}/branches`),
  getOne: (id) => api.get(`/banks/${id}`),
};

export const loanTermAPI = {
  getForMonths: (months) => api.get('/loan-term-configs/for-months', { params: { months } }),
  estimateCommission: (params) => api.get('/ctv/commissions/estimate', { params }),
};

export default api;
